/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable object-shorthand */
import { Image, StyleSheet } from 'react-native';
import * as Analytics from 'expo-firebase-analytics';
import { useMemo } from 'react';
import { TextWrap as Text, View, VBox, SplitBox, styles as sc } from '../../components/Themed';
import { RootStackScreenProps } from '../../navigation/navigationTypes';
import Colors from '../../constants/Colors';
import TextButton from '../../components/elements/TextButton';
import TiledImages from '../../components/site-components/TiledImages';
// Image assets
import useWindowDimensions from '../../hooks/useWindowDimensions';
import SnapCalorieSitePage from '../../components/SnapCalorieSitePage';
import Api from '../../constants/Api';

export function BetaUsersElement({ navToSubPage }: { navToSubPage: () => void }): JSX.Element {
  return (
    <VBox style={styles.darkRowElement} key="BetaUsersElement">
      <Text style={[sc.h3, { textAlign: 'center' }]}>Current Users</Text>
      <Text style={[sc.blockParagraph, { textAlign: 'center' }]}>Thank you for being a part of our community.</Text>
      <Text style={[sc.link, { textAlign: 'center' }]} onPress={navToSubPage}>
        Manage Subscription
      </Text>
    </VBox>
  );
}

// eslint-disable-next-line prettier/prettier
export function JoinTheCommunityElement({ navToConversionFlow }: { navToConversionFlow: () => void }): JSX.Element {
  return (
    <View style={{ alignItems: 'center', paddingTop: 40 }}>
      <Text style={sc.h2}>Join Now</Text>
      <Text style={[sc.blockParagraph, { textAlign: 'center' }]}>
        Our nutrition experts assisted with the SnapCalorie algorithm achieve <b>more than twice the accuracy</b> of the averge 
        user logging their own meals.
      </Text>
      <Text style={[sc.blockParagraph, { textAlign: 'center' }]}>
        Complete a short survey to determine if you are a fit for the SnapCalorie program.
      </Text>
      <Text style={sc.blockParagraph}>&nbsp;</Text>
      <TextButton
        containerStyle={styles.buttonContainer}
        textStyle={styles.buttonText}
        onPress={navToConversionFlow}
        text="Continue"
      />
    </View>
  );
}

const APP_PREVIEW_IMAGE = require('../../../assets/images/app_preview.png');

export function AppPreviewElement(): JSX.Element {
  return (
    <View style={{ alignItems: 'center', paddingTop: 40 }} key="AppPreviewElement">
      <Image source={APP_PREVIEW_IMAGE} style={{ alignSelf: 'center', width: 300, height: 400, borderWidth: 1 }} />
    </View>
  );
}

export function TestimonialsElement(): JSX.Element {
  return (
    <VBox style={styles.darkRowElement} key="TestimonialsElement">
      <Text style={[sc.h2, { textAlign: 'center', marginBottom: 20 }]}>What do our users say?</Text>
      <Text style={[sc.blockParagraph, { flex: 1, textAlign: 'center' }]}>
        "The most accurate tracking app I have ever used."
      </Text>
      <Text style={[sc.blockParagraph, { flex: 1, textAlign: 'center' }]}>
        "I've lost 6 pounds already this month!"
      </Text>
      <Text style={[sc.blockParagraph, { flex: 1, textAlign: 'center' }]}>
        "I have no idea how everyone wouldn’t want to use this."
      </Text>
    </VBox>
  );
}

export function WelcomeScreenElement({ navToConversionFlow }: { navToConversionFlow: () => void }): JSX.Element {
  return (
    <View key="WelcomeScreenElement">
    <Text style={sc.h1}>Your Personal Nutrition Assistant</Text>
      <Text style={sc.blockParagraph}>
          Use your phone to take a picture of <b>any meal</b> and a real live <b>nutrition expert</b> will label your food
          plate for you! SnapCalorie also uses your phone's <b>depth sensor</b> and our proprietary algorithm to measure accurate portion sizes.{'\n'}
          {'\n'}Answer a few questions about your habits and goals to set targets and start your journey to better health.
      </Text>
      <TextButton
        textStyle={{
          color: '#F8F5F4',
          fontSize: 20,
          fontWeight: '600',
          lineHeight: 25,
          letterSpacing: -0.29,
        }}
        style={styles.buttonContainer}
        text="Continue"
        onPress={navToConversionFlow}
      />
    </View>
  );
}

export default function HomeScreen({ navigation }: RootStackScreenProps<'Home'>) {
  const screenDimensions = useWindowDimensions();

  const IsColumnFormat = useMemo(() => {
    return screenDimensions.width < 1000;
  }, [screenDimensions.width]);

  const navToConversionFlow = () => {
    Analytics.logEvent('conversion-continue');
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-694660706/yi-CCJigu4EYEOLcnssC'
      });
    }
    navigation.navigate('Survey', { id: 'FitnessGoal' });
  };

  Analytics.logEvent('conversion-home');
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-694660706/huVXCOCAuoEYEOLcnssC'
    });
  }

  return (
    <SnapCalorieSitePage headerLinks={{}}>
      <SplitBox style={styles.splitBox} flex={[0.6, 0.4]} mobileView={IsColumnFormat}>
        <WelcomeScreenElement navToConversionFlow={navToConversionFlow} />
      </SplitBox>
      <TiledImages />
      <TestimonialsElement />
      <SplitBox style={styles.splitBox} flex={[0.4, 0.6]} mobileView={IsColumnFormat} reverseColumn>
        <AppPreviewElement />
        <JoinTheCommunityElement navToConversionFlow={navToConversionFlow} />
      </SplitBox>
      <BetaUsersElement navToSubPage={() => navigation.navigate('Subscription')} />
    </SnapCalorieSitePage>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    alignSelf: 'center',
  },
  splitBox: {
    marginHorizontal: 100,
    marginVertical: 40,
    maxWidth: 1200,
    alignSelf: 'center',
  },
  darkRowElement: {
    marginTop: 80,
    paddingVertical: 60,
    paddingHorizontal: 120,
    backgroundColor: Colors.snapCalorie.background,
    width: '100%',
  },
  buttonContainer: {
    backgroundColor: Colors.snapCalorie.orange, // '#008046', // SnapCalorie Green(?)
    height: 50,
    width: 290,
    borderRadius: 21,
    marginBottom: 24,
    alignSelf: 'center',
  },
  buttonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 24,
  },
});
